import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { IEmployee } from 'src/@types/apiResponseTypes';

import { EMPTY_SEAT_COLOR, TEAM_COLOR_LIST } from '../constants/teamColors';
import { SeatInfo } from '../types/seatTypes';

interface SeatEditDialogProps {
  open: boolean;
  seat: SeatInfo | null;
  employeeList: IEmployee[];
  onClose: () => void;
  onConfirm: (updatedSeat: SeatInfo) => void;
}

const SeatEditDialog = ({ open, seat, employeeList, onClose, onConfirm }: SeatEditDialogProps) => {
  const [selectedEmployee, setSelectedEmployee] = useState<IEmployee | null>(null);
  const [selectedColor, setSelectedColor] = useState<string>(EMPTY_SEAT_COLOR);

  // 공석 옵션 추가
  const employeeOptions = useMemo(() => {
    const emptyOption: IEmployee = {
      userSid: 0,
      loginId: '',
      regDt: 0,
      updDt: 0,
      empNm: '공석',
      deptSid: 0,
      empPosCd: null,
      empJobrespCd: null,
      hireDay: null,
      tmntYn: false,
      leaveYn: false,
      tmntDay: null,
      birth: null,
      genderTypeCd: null,
      phone: null,
      ntisRegNo: null,
      careerTotYears: null,
      careerSmlrYears: null,
      user: {
        userSid: 0,
        userNm: '공석',
        loginId: '',
        userGroupList: [],
        userGroupSidList: [],
      },
      profileImgUrl: null,
    };
    return [emptyOption, ...employeeList.filter((emp) => !emp.tmntYn)];
  }, [employeeList]);

  // 좌석 선택 시 기존 데이터 설정
  useEffect(() => {
    if (seat && open) {
      setSelectedColor(seat.teamColor || EMPTY_SEAT_COLOR);

      if (seat.employeeId && seat.employeeId !== 0) {
        const emp = employeeList.find((e) => e.userSid === seat.employeeId);
        setSelectedEmployee(emp || null);
      } else {
        setSelectedEmployee(null);
      }
    }
  }, [seat, open, employeeList]);

  const handleConfirm = () => {
    if (!seat) return;

    const updatedSeat: SeatInfo = {
      ...seat,
      employeeId: selectedEmployee?.userSid,
      employeeName: selectedEmployee?.userSid === 0 ? undefined : selectedEmployee?.empNm,
      employeePosition: selectedEmployee?.empPosCd?.value || undefined,
      teamColor: selectedColor,
      teamName: TEAM_COLOR_LIST.find((t) => t.color === selectedColor)?.teamName,
      profileImgUrl: selectedEmployee?.profileImgUrl,
    };

    onConfirm(updatedSeat);
  };

  const handleClose = () => {
    setSelectedEmployee(null);
    setSelectedColor(EMPTY_SEAT_COLOR);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">좌석 설정</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* 직원 검색 */}
          <Autocomplete
            options={employeeOptions}
            value={selectedEmployee}
            onChange={(_, value) => setSelectedEmployee(value)}
            getOptionLabel={(option) => {
              if (option.userSid === 0) return '공석';
              return `${option.empNm} (${option.empPosCd?.value || ''})`;
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const searchTerm = inputValue.toLowerCase();
              return options.filter((option) => {
                if (option.userSid === 0) return '공석'.includes(searchTerm);
                const name = option.empNm?.toLowerCase() || '';
                const position = option.empPosCd?.value?.toLowerCase() || '';
                return name.includes(searchTerm) || position.includes(searchTerm);
              });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.userSid}>
                <Stack>
                  <Typography variant="body2">{option.empNm}</Typography>
                  {option.userSid !== 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {option.empPosCd?.value || ''}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="직원 검색"
                placeholder="이름을 입력하세요"
                style={{ top: 10 }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.userSid === value.userSid}
          />

          {/* 색상 및 팀 정보 선택 */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              팀 색상 선택
            </Typography>
            <Stack spacing={1}>
              {TEAM_COLOR_LIST.map((team) => (
                <ColorOptionItem
                  key={team.fullName || team.teamName}
                  selected={selectedColor === team.color}
                  onClick={() => setSelectedColor(team.color)}
                >
                  <Radio checked={selectedColor === team.color} size="small" sx={{ p: 0.5 }} />
                  <ColorCircle color={team.color} />
                  <Stack flex={1}>
                    <Typography variant="body2" fontWeight="medium">
                      {team.teamName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {team.department}
                    </Typography>
                  </Stack>
                </ColorOptionItem>
              ))}
              {/* 공석 옵션 */}
              <ColorOptionItem
                selected={selectedColor === EMPTY_SEAT_COLOR}
                onClick={() => setSelectedColor(EMPTY_SEAT_COLOR)}
              >
                <Radio checked={selectedColor === EMPTY_SEAT_COLOR} size="small" sx={{ p: 0.5 }} />
                <ColorCircle color={EMPTY_SEAT_COLOR} />
                <Stack flex={1}>
                  <Typography variant="body2" fontWeight="medium">
                    공석
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    빈 좌석
                  </Typography>
                </Stack>
              </ColorOptionItem>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          취소
        </Button>
        <Button variant="contained" onClick={handleConfirm}>
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeatEditDialog;

const ColorOptionItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const ColorCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color, theme }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: color,
  border: `2px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));
